import styled from '@emotion/styled';
import React, { FC, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import { PageContentHorizontalAlignment } from 'src/generated/webapp_gql';
import inlineSparkle from 'src/public/img/inline-sparkle.svg';
import { color, contentStyle, font, headingStyle } from 'src/shared/styles/variables';

/**
 * Features added or removed here should be kept in-sync with
 * the MarkdownRenderer in Mobile:
 *
 * https://gitlab.com/modernhealth/mobile/-/blob/main/src/modules/programs/components/MarkdownRenderer.tsx
 */

const Blockquote = styled.div({
    backgroundColor: color.blockQuoteBackground,
    borderRadius: 6,
    paddingTop: 12,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 12,
    marginVertical: 12,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    blockquote: {
        paddingLeft: 8,
    },
    img: {
        marginTop: 4,
        marginRight: 6,
    },
});

const components = {
    blockquote: ({ children = [] as ReactNode[] }) => {
        return (
            <Blockquote>
                <img src={inlineSparkle} alt="" />
                <blockquote children={children} />
            </Blockquote>
        );
    },
};

const Container = styled.div<{
    textHorizontalAlignment?: PageContentHorizontalAlignment;
}>(
    {
        fontSize: font.size.medium,
        '*': {
            fontWeight: 400,
            letterSpacing: 'normal',
        },
        'p, span, h1, h2, h3, h4, h5, h6': {
            color: color.ebonyClay,
        },
        'h1, h2, h3': {
            fontWeight: 900,
        },
        strong: {
            fontWeight: 800,
        },
        ul: {
            listStyleType: 'disc',
        },
        'ol, ul': {
            textAlign: 'start',
        },
    },
    {
        'h1, h2, h3, h4, h5, h6': {
            paddingBottom: 8,
        },
        h1: headingStyle.MarkdownH1,
        h2: headingStyle.MarkdownH2,
        h3: headingStyle.MarkdownH3,
        h4: contentStyle.MarkdownMedium,
        h5: contentStyle.MarkdownMedium,
        h6: contentStyle.MarkdownMedium,
        p: {
            ...contentStyle.MarkdownMedium,
            paddingBottom: 8,
        },
        span: contentStyle.MarkdownMedium,
        em: {
            fontStyle: 'italic',
        },
        a: {
            textDecorationLine: 'underline',
            fontWeight: 800,
        },
        u: {
            textDecorationLine: 'underline',
        },
        'ul > li': {
            display: 'list-item',
            paddingLeft: '1.5rem',
        },
        'ol > li': {
            display: 'list-item',
            listStyleType: 'decimal',
        },
        list: {
            width: '100%',
        },
    },
    ({ textHorizontalAlignment }) => {
        switch (textHorizontalAlignment) {
            case PageContentHorizontalAlignment.Center:
                return {
                    textAlign: 'center',
                };
            case PageContentHorizontalAlignment.Start:
            default:
                return {
                    textAlign: 'start',
                };
        }
    },
);

interface MarkdownProps {
    text: string;
    textHorizontalAlignment?: PageContentHorizontalAlignment;
}

export const Markdown: FC<MarkdownProps> = ({ text, textHorizontalAlignment }) => {
    return (
        <Container textHorizontalAlignment={textHorizontalAlignment}>
            <ReactMarkdown className="markdown" components={components} children={text} />
        </Container>
    );
};
